import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';

export default function Verify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await confirmSignUp({
        username: email.toLowerCase(),
        confirmationCode: code.trim(),
      });
      navigate('/login?verified=1');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      if (message.includes('Invalid verification code') || message.includes('CodeMismatchException')) {
        setError('That code is incorrect. Double-check and try again.');
      } else if (message.includes('expired') || message.includes('ExpiredCodeException')) {
        setError('This code has expired. Request a new one below.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    try {
      await resendSignUpCode({ username: email.toLowerCase() });
      setResent(true);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    }
  }

  return (
    <AuthLayout>
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">Check your email</h1>
      <p className="text-sm text-gray-600 mb-8">
        We sent a 6-digit code to <strong>{email || 'your email'}</strong>. Enter it below to
        verify your account.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
            Verification code
          </label>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
        )}
        {resent && (
          <p className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
            New code sent — check your inbox.
          </p>
        )}

        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Verifying…' : 'Verify email'}
        </button>
      </form>

      <div className="mt-6 flex flex-col items-center gap-2 text-sm text-gray-600">
        <button
          type="button"
          onClick={handleResend}
          className="text-indigo-600 hover:text-indigo-700"
        >
          Resend code
        </button>
        <Link to="/login" className="text-gray-500 hover:text-gray-700">
          Back to sign in
        </Link>
      </div>
    </AuthLayout>
  );
}

function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="mb-8">
        <span className="text-xl font-bold text-gray-900 tracking-tight">Slingshotio</span>
      </div>
      <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        {children}
      </div>
    </div>
  );
}
